import React, { useState, useEffect, useMemo, useRef } from 'react';
import DynamicForm from '../../components/DynamicForm/DynamicForm';
import scriptRunnerService from '../../services/scriptRunner.service';
import logger from '../../utils/logger';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { MaterialReactTable } from 'material-react-table';
import Typography from '@mui/material/Typography';
import DownloadIcon from '@mui/icons-material/Download';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import styles from './ScriptRunner.module.css';

import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Tooltip,
	Paper,
	Chip,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Divider,
	Snackbar,
	Alert,
	LinearProgress
} from '@mui/material';

const DEFAULT_PAGE_SIZE = 15;
const REFRESH_INTERVAL = 600000; //10 minutes

function ScriptRunner(props) {
	const [jobsData, setJobsData] = useState([]);
	const [options, setOptions] = useState([]);
	const [selectedOption, setSelectedOption] = useState(null);
	const [activeScripts, setActiveScripts] = useState([]);
	const [input, setInput] = useState([]);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	const [openDialog, setOpenDialog] = useState(false);
	const [paginationInfo, setPaginationInfo] = useState({
		totalCount: 0,
		totalPages: 0,
		hasNextPage: false,
		hasPrevPage: false
	});
	const formRef = useRef(null);
	const refreshTimerRef = useRef(null);
	// State for success message snackbar
	const [openSnackbar, setOpenSnackbar] = useState(false);
	const [snackbarMessage, setSnackbarMessage] = useState('');
	const [snackbarSeverity, setSnackbarSeverity] = useState('success');
	const [isLoading, setIsLoading] = useState(false);
	const [isRefreshing, setIsRefreshing] = useState(false);
	
	// Search state
	const [searchTerm, setSearchTerm] = useState('');
	const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
	
	// Sorting state
	const [sorting, setSorting] = useState([]);
	
	// Inline debounce logic for search
	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearchTerm(searchTerm);
		}, 500);

		return () => {
			clearTimeout(timer);
		};
	}, [searchTerm]);

	useEffect(() => {
		fetchData();
		// Set up periodic refresh
		refreshTimerRef.current = setInterval(() => {
			fetchData(false);
		}, REFRESH_INTERVAL);

		// Clean up the timer when component unmounts
		return () => {
			if (refreshTimerRef.current) {
				clearInterval(refreshTimerRef.current);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page, pageSize, debouncedSearchTerm, sorting]);

	const handleSelectChange = (event, value) => {
		setSelectedOption(value);
		setInput(
			activeScripts.find((script) => script._id === value?.id)?.input || []
		);
	};

	const handlePrevPage = () => {
		if (page > 1) {
			setPage(page - 1);
		}
	};

	const handleNextPage = () => {
		if (paginationInfo.hasNextPage) {
			setPage(page + 1);
		}
	};

	const handleDownloadClick = (id) => {
		if (id !== null) {
			scriptRunnerService
				.downloadFiles(id)
				.then((response) => {
					setSnackbarMessage('File downloaded successfully');
					setSnackbarSeverity('success');
					setOpenSnackbar(true);
				})
				.catch((error) => {
					setSnackbarMessage('Error downloading the file: ' + error.message);
					setSnackbarSeverity('error');
					setOpenSnackbar(true);
				});
		}
	};

	const handlePageSizeChange = (event) => {
		setPageSize(parseInt(event.target.value, 10));
		setPage(1); // Reset to first page when changing page size
	};

	const handleOpenDialog = () => {
		setSelectedOption(null);
		setInput([]);
		setOpenDialog(true);
	};

	const handleCloseDialog = () => {
		setOpenDialog(false);
	};

	const handleSubmitJobClick = () => {
		// Validate form through the DynamicForm ref before closing dialog
		if (formRef.current && typeof formRef.current.validateAndSubmit === 'function') {
			const isValid = formRef.current.validateAndSubmit();
			
			// Only close dialog and show message if validation passed
			if (isValid) {
				setOpenDialog(false);
				
				// Show a message that job creation has started
				setSnackbarMessage('Job creation started...');
				setSnackbarSeverity('info');
				setOpenSnackbar(true);
			}
			// If not valid, the form component will display appropriate error messages
		}
	};

	const handleCloseSnackbar = (event, reason) => {
		if (reason === 'clickaway') {
			return;
		}
		setOpenSnackbar(false);
	};
	
	// Manual refresh button handler
	const handleManualRefresh = () => {
		setIsRefreshing(true);
		fetchData(true).then(() => {
			setIsRefreshing(false);
			setSnackbarMessage('Data has been refreshed');
			setSnackbarSeverity('success');
			setOpenSnackbar(true);
		});
	};

	// Success callback for when job is created successfully
	const handleJobCreated = () => {
		setOpenDialog(false);
		setSnackbarMessage('Job created successfully! Watch the status column for updates.');
		setSnackbarSeverity('success');
		setOpenSnackbar(true);
		fetchData(); // Refresh data immediately after submission
	};

	const handleCellRender = (value) => {
		if (!value) return '';
		
		let stringValue = typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value);
		const truncatedString = stringValue.length > 50 ? stringValue.substring(0, 50) + '...' : stringValue;
		
		return (
			<Tooltip
				arrow
				placement="left"
				classes={{
					popper: styles.jsonTooltip
				}}
				title={
					<pre className={styles.jsonTooltipContent}>
						{stringValue}
					</pre>
				}
			>
				<span className={styles.truncatedCell}>{truncatedString}</span>
			</Tooltip>
		);
	};

	const columns = useMemo(
		() => [
			{
				header: 'Download',
				size: 80,
				Cell: ({ row }) => (
					<div className={styles.actionsCell}>
						{row.original.status === 'done' ? (
							<Tooltip arrow placement='right' title='Download'>
								<IconButton
									className={styles.downloadButton}
									onClick={() => handleDownloadClick(row.original._id)}
									size="small"
								>
									<DownloadIcon />
								</IconButton>
							</Tooltip>
						) : null}
					</div>
				),
				},
			{
				header: 'Id',
				accessorKey: '_id',
				size: 180,
			},
			{
				header: 'Date Run (Local)',
				accessorKey: 'created',
				size: 150,
				Cell: ({ cell }) => {
					const value = cell.getValue();
					if (!value) return 'N/A';
					try {
						const date = new Date(value);
						// Use local date and time format based on user's browser locale
						return date.toLocaleString(undefined, {
							dateStyle: 'medium',
							timeStyle: 'medium'
						});
					} catch (e) {
						return value || 'N/A';
					}
				}
			},
			{
				header: 'Run By',
				accessorKey: 'createdBy',
				Cell: ({ cell }) => <span>{cell.getValue() || 'N/A'}</span>,
				size: 150,
			},
			{
				header: 'Script',
				accessorKey: 'script',
				size: 150,
			},
			{
				header: 'Status',
				accessorKey: 'status',
				Cell: ({ cell }) => {
					const value = cell.getValue();
					
					// Different styling for different status values
					let chipClassName;
					let progressBar = null;
					let errorIcon = null;
					
					switch(value) {
						case 'initial':
						case 'running':
							chipClassName = value === 'initial' ? styles.statusInitial : styles.statusRunning;
							progressBar = <LinearProgress style={{ width: '60px', height: '4px' }} color={value === 'initial' ? "primary" : "secondary"} />;
							break;
						case 'errored':
							chipClassName = styles.statusError;
							errorIcon = <Typography component="span" sx={{ color: '#d32f2f', fontWeight: 'bold', ml: 1 }}>!</Typography>;
							break;
						case 'done':
							chipClassName = styles.statusDone;
							break;
						default:
							chipClassName = styles.statusDefault;
					}
					
					return (
						<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
							<Chip 
								label={value}
								variant="outlined"
								size="small"
								className={`${styles.statusChip} ${chipClassName}`}
							/>
							{(value === 'initial' || value === 'running') && progressBar}
							{value === 'errored' && errorIcon}
						</div>
					);
				},
				size: 100,
			},
			{
				header: 'Input',
				accessorKey: 'input',
				size: 200,
				Cell: ({ cell }) => handleCellRender(cell.getValue()),
				enableSorting: false, // Disable sorting for Input column
			},
			{
				header: 'Error',
				accessorKey: 'error',
				size: 200,
				Cell: ({ cell }) => handleCellRender(cell.getValue()),
				enableSorting: false, // Disable sorting for Error column
			}
		],
		[]
	);

	const helpTooltipContent = (
		<div>
			<Typography variant="subtitle2" gutterBottom>Table Features:</Typography>
			<ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
				<li>Click search icon to show/hide search bar</li>
				<li>Search by: ID, script, status, or user</li>
				<li>Drag column headers to reorder columns</li>
				<li>Click and drag column borders to resize</li>
				<li>Click column headers to sort (except Input and Error columns)</li>
				<li>Hover over Input/Error cells to see full content</li>
			</ul>
		</div>
	);

	const fetchData = async (showLoader = true) => {
		if (showLoader) {
			setIsLoading(true);
		}

		try {
			// Fetch scripts
			const scriptsResponse = await scriptRunnerService.getScripts();
			setActiveScripts(scriptsResponse);
			const autocompleteOptions = scriptsResponse.map((script) => ({
				id: script._id,
				label: script.name,
			}));
			setOptions(autocompleteOptions);
			
			// Prepare sorting parameters
			let sortBy = '_id'; // default sort field
			let sortOrder = 'desc'; // default sort order (newest first)
			
			if (sorting.length > 0) {
				const sortField = sorting[0].id;
				sortOrder = sorting[0].desc ? 'desc' : 'asc';
				
				// Map column ids to database field names
				switch (sortField) {
					case '_id':
						sortBy = '_id';
						break;
					case 'created':
						sortBy = 'created';
						break;
					case 'createdBy':
						sortBy = 'createdBy';
						break;
					case 'script':
						sortBy = 'script';
						break;
					case 'status':
						sortBy = 'status';
						break;
					default:
						sortBy = '_id';
				}
			}
			
			// Fetch jobs with search and sorting parameters
			const jobsResponse = await scriptRunnerService.getJobs(page, pageSize, debouncedSearchTerm, sortBy, sortOrder);
			const jobs = jobsResponse.jobs || [];
			const jobsArray = Array.isArray(jobs) ? jobs : [];
			setJobsData(jobsArray);
			
			// Update pagination info
			if (jobsResponse.pagination) {
				setPaginationInfo({
					totalCount: jobsResponse.pagination.totalCount || 0,
					totalPages: jobsResponse.pagination.totalPages || 0,
					hasNextPage: jobsResponse.pagination.hasNextPage || false,
					hasPrevPage: jobsResponse.pagination.hasPrevPage || false
				});
			}
		} catch (error) {
			logger.error('Error fetching data:', error);
			if (showLoader) {
				setSnackbarMessage('Error refreshing data: ' + error.message);
				setSnackbarSeverity('error');
				setOpenSnackbar(true);
			}
		} finally {
			if (showLoader) {
				setIsLoading(false);
			}
		}
		
		// Return a promise for chaining
		return Promise.resolve();
	};

	// Search handler for debounced search
	// Render the pagination controls
	const renderPagination = () => {
		return (
			<div className={styles.paginationContainer}>
				<div className={styles.paginationLeft}>
					<Button 
						variant="contained"
						onClick={handlePrevPage}
						disabled={page === 1}
						className={styles.paginationButton}
					>
						Previous Page
					</Button>
					
					<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
						<Chip 
							label={`Page ${page}${paginationInfo.totalPages > 0 ? ` of ${paginationInfo.totalPages}` : ''}`} 
							variant="outlined"
							className={styles.pageIndicator}
						/>
						{paginationInfo.totalCount > 0 && (
							<Chip 
								label={`${paginationInfo.totalCount} total`} 
								variant="outlined"
								size="small"
								color="primary"
							/>
						)}
						<Tooltip 
							arrow
							placement="right"
							classes={{
								popper: styles.helpTooltip
							}}
							title={helpTooltipContent}
						>
							<IconButton className={styles.helpButton} size="small">
								<HelpOutlineIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					</div>
				</div>
				
				<div className={styles.paginationRight}>
					<Button
						variant="outlined"
						onClick={handleManualRefresh}
						startIcon={<RefreshIcon className={isRefreshing ? styles.spinIcon : ''} />}
						disabled={isRefreshing}
						className={styles.refreshButton}
					>
						{isRefreshing ? 'Refreshing...' : 'Refresh'}
					</Button>
					
					<FormControl className={styles.pageSizeSelector} size="small">
						<InputLabel id="page-size-select-label">Page Size</InputLabel>
						<Select
							labelId="page-size-select-label"
							id="page-size-select"
							value={pageSize}
							label="Page Size"
							onChange={handlePageSizeChange}
						>
							<MenuItem value={10}>10</MenuItem>
							<MenuItem value={15}>15</MenuItem>
							<MenuItem value={25}>25</MenuItem>
							<MenuItem value={50}>50</MenuItem>
							<MenuItem value={100}>100</MenuItem>
						</Select>
					</FormControl>
					
					<Button 
						variant="contained"
						onClick={handleNextPage}
						disabled={!paginationInfo.hasNextPage}
						className={styles.paginationButton}
					>
						Next Page
					</Button>
				</div>
			</div>
		);
	};

	return (
		<div className={styles.container}>
			{/* Improved Dialog for creating a new job */}
			<Dialog
				open={openDialog}
				onClose={handleCloseDialog}
				fullWidth
				maxWidth='sm'
				PaperProps={{
					style: { borderRadius: '8px' }
				}}
			>
				<DialogTitle className={styles.dialogTitle}>
					Create New Job
				</DialogTitle>
				
				<DialogContent className={styles.dialogContent}>
					<div className={styles.dialogFormContainer}>
						<div>
							<Typography className={styles.formSectionTitle}>
								1. Select Script
							</Typography>
							<Autocomplete
								disablePortal
								id='selectScript'
								options={options}
								fullWidth
								value={selectedOption}
								onChange={handleSelectChange}
								renderInput={(params) => (
									<TextField 
										{...params} 
										label='Choose the script to run' 
										variant="outlined" 
										fullWidth 
									/>
								)}
							/>
						</div>
						
						{selectedOption && (
							<>
								<Divider sx={{ my: 3 }}/>
								<div>
									<Typography className={styles.formSectionTitle}>
										2. Configure Parameters
									</Typography>
									<Box sx={{ mt: 2 }}>
										<DynamicForm 
											ref={formRef}
											input={input} 
											selectedScript={selectedOption?.label}
											onSuccess={handleJobCreated}
										/>
									</Box>
								</div>
							</>
						)}
					</div>
				</DialogContent>
				
				<DialogActions className={styles.dialogActions}>
					<Button 
						onClick={handleCloseDialog} 
						className={styles.dialogCancelButton}
					>
						Cancel
					</Button>
					<Button
						onClick={handleSubmitJobClick}
						variant="contained"
						disabled={!selectedOption}
						className={styles.dialogSubmitButton}
					>
						Create Job
					</Button>
				</DialogActions>
			</Dialog>
			
			<div className={styles.headerContainer}>
				<div className={styles.headerRow}>
					<Button 
						className={styles.backButton}
						onClick={() => window.history.back()}
						startIcon={<ArrowBackIcon />}
					>
						Back
					</Button>
					<Typography variant='h6' color='black' className={styles.tableHeader}>
						Script Runner Jobs
					</Typography>
				</div>
				
				<div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px', marginBottom: '16px' }}>
					<Button
						variant='contained'
						onClick={handleOpenDialog}
						className={styles.createButton}
						startIcon={<AddIcon />}
					>
						Create a new Job
					</Button>
				</div>
			</div>
			
			<Paper className={styles.tableContainer}>
				{renderPagination()}
				
				{/* Search Results Info */}
				{debouncedSearchTerm && (
					<div className={styles.searchResultsInfo}>
						<Typography variant="body2" color="textSecondary">
							{paginationInfo.totalCount === 0 
								? `No results found for "${debouncedSearchTerm}"`
								: `Showing ${jobsData.length} of ${paginationInfo.totalCount} results for "${debouncedSearchTerm}" (Page ${page}${paginationInfo.totalPages > 0 ? ` of ${paginationInfo.totalPages}` : ''})`
							}
						</Typography>
					</div>
				)}
				
				{isLoading && (
					<LinearProgress sx={{ mt: 1 }} />
				)}
				
				<div className={styles.tableWrapper}>
					<MaterialReactTable
						columns={columns}
						data={jobsData}
						enablePagination={false}
						enableColumnOrdering
						enableColumnResizing
						enableStickyHeader
						enableGlobalFilter={true}
						enableColumnFilters={false}
						enableSorting={true}
						manualSorting={true} // Enable server-side sorting
						globalFilterFn={() => true} // We handle filtering server-side
						onGlobalFilterChange={(value) => {
							const newSearchTerm = value || '';
							setSearchTerm(newSearchTerm);
							
							// Reset to first page when searching or when clearing search
							if (page !== 1) {
								setPage(1);
							}
						}}
						onSortingChange={setSorting}
						state={{
							globalFilter: searchTerm,
							isLoading: false, // We handle loading state externally
							sorting: sorting, // Controlled sorting state
						}}
						muiSearchTextFieldProps={{
							placeholder: 'Search by ID, script, status, or user...',
							sx: { 
								minWidth: '300px',
								marginLeft: 'auto',
								'& .MuiInputBase-root': {
									transition: 'all 0.3s ease-in-out'
								}
							},
							variant: 'outlined',
							size: 'small'
						}}
						muiTopToolbarProps={{
							sx: {
								'& .MuiToolbar-root': {
									justifyContent: 'flex-end'
								}
							}
						}}
						muiTableContainerProps={{ 
							className: styles.expandableTable
						}}
						muiTablePaperProps={{ elevation: 0 }}
						initialState={{ 
							density: 'compact',
							columnVisibility: { 
								_id: true
							},
							showGlobalFilter: false // Start with search hidden
						}}
						enableGlobalFilterModes
						positionGlobalFilter="right" // Position search on the right side
					/>
				</div>
			</Paper>
			
			{/* Success/Error notifications */}
			<Snackbar
				open={openSnackbar}
				autoHideDuration={6000}
				onClose={handleCloseSnackbar}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
			>
				<Alert 
					onClose={handleCloseSnackbar} 
					severity={snackbarSeverity}
					variant="filled"
					sx={{ width: '100%' }}
				>
					{snackbarMessage}
				</Alert>
			</Snackbar>
		</div>
	);
}

ScriptRunner.propTypes = {};

ScriptRunner.defaultProps = {};

export default ScriptRunner;
